import { useCallback, useEffect, useState } from "react";
import { App, Button, Descriptions, Form, Input, Modal, Result, Segmented, Space, Switch, Table, Tabs, Tag, Upload } from "antd";
import type { ArrivalImportRow, OpeningImportRow, SkuImportRow } from "@shoe-trace/shared";
import {
  commitArrival,
  commitImport,
  createSku,
  getArrival,
  listArrivals,
  previewImport,
  searchSkus,
  updateSku,
  type ArrivalLineRow,
  type ArrivalSummary,
  type ImportIssue,
  type SkuRow,
} from "../api/client";
import { formatDay, formatWhen, LoadFail, PageSpin } from "../components/widgets";
import { canManage, useSession } from "../session";

interface SkuFormValues {
  humanCode: string;
  name: string;
  color: string;
  sizeUs: string;
  sizeEu: string;
  trackUnit?: boolean;
}

const TEMPLATES: Record<"sku" | "opening", string> = {
  sku: "SKU,款式名称,颜色,美码,欧码,是否试点逐双追踪\n",
  opening: "SKU,批次号,入库日,库位编码,双数\n",
};

const ARRIVAL_TEMPLATE = "SKU,双数,入库日\nST1920white34,4,2026-10-09\n";

const ARRIVAL_STATUS: Record<string, string> = {
  OPEN: "待锁定",
  LOCKED: "号段已锁定",
  CLOSED: "已收工",
};

export function Skus(): JSX.Element {
  const { message } = App.useApp();
  const { user } = useSession();
  const manager = canManage(user);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<SkuRow[]>([]);
  const [arrivals, setArrivals] = useState<ArrivalSummary[]>([]);
  const [arrivalTotal, setArrivalTotal] = useState(0);
  const [arrivalPage, setArrivalPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [skuOpen, setSkuOpen] = useState(false);
  const [editing, setEditing] = useState<SkuRow | null>(null);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [kind, setKind] = useState<"sku" | "opening">("sku");
  const [previewRows, setPreviewRows] = useState<Array<SkuImportRow | OpeningImportRow>>([]);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [importTotal, setImportTotal] = useState(0);
  const [importDone, setImportDone] = useState<{ written: number; skipped: number } | null>(null);
  const [arrivalRows, setArrivalRows] = useState<ArrivalImportRow[]>([]);
  const [arrivalIssues, setArrivalIssues] = useState<ImportIssue[]>([]);
  const [arrivalPreviewTotal, setArrivalPreviewTotal] = useState(0);
  const [skuForm] = Form.useForm<SkuFormValues>();

  const load = useCallback(() => {
    setLoading(true);
    void Promise.all([searchSkus(q, page, pageSize), listArrivals(arrivalPage, 20)])
      .then(([skuPage, arrivalPageResult]) => {
        setRows(skuPage.rows);
        setTotal(skuPage.total);
        setArrivals(arrivalPageResult.rows);
        setArrivalTotal(arrivalPageResult.total);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [arrivalPage, page, pageSize, q]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && rows.length === 0 && arrivals.length === 0) return <PageSpin tip="正在读取 SKU 档案…" />;
  if (failed && rows.length === 0) return <LoadFail title="SKU 档案没有加载出来。" onRetry={load} />;

  return (
    <>
      <Tabs
        items={[
          {
            key: "sku",
            label: "SKU",
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Input.Search
                    allowClear
                    placeholder="搜 SKU 或 8 位内部编码"
                    enterButton="按这个条件查找 SKU"
                    onSearch={(value) => {
                      setPage(1);
                      setQ(value);
                    }}
                    style={{ width: 420 }}
                  />
                  {manager ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        setEditing(null);
                        skuForm.resetFields();
                        setSkuOpen(true);
                      }}
                    >
                      新建 SKU
                    </Button>
                  ) : null}
                </Space>
                <Table
                  rowKey="internalCode"
                  loading={loading}
                  dataSource={rows}
                  locale={{
                    emptyText: manager
                      ? "还没有 SKU。请主管导入清单，或点「新建 SKU」写第一条。"
                      : "还没有 SKU。请主管导入清单。",
                  }}
                  pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    showTotal: (count) => `共 ${count} 个 SKU`,
                    onChange: (next, size) => {
                      setPage(next);
                      setPageSize(size);
                    },
                  }}
                  columns={[
                    { title: "SKU", dataIndex: "humanCode" },
                    { title: "内部编码", dataIndex: "internalCode" },
                    { title: "款式名称", dataIndex: "name" },
                    { title: "颜色", dataIndex: "color" },
                    { title: "美码", dataIndex: "sizeUs" },
                    { title: "欧码", dataIndex: "sizeEu" },
                    {
                      title: "是否试点逐双追踪",
                      dataIndex: "trackUnit",
                      render: (value: boolean) => <Tag color={value ? "processing" : "default"}>{value ? "是" : "否"}</Tag>,
                    },
                    {
                      title: "操作",
                      render: (_value: unknown, row: SkuRow) =>
                        manager ? (
                          <Button
                            type="link"
                            onClick={() => {
                              setEditing(row);
                              skuForm.setFieldsValue(row);
                              setSkuOpen(true);
                            }}
                          >
                            修改
                          </Button>
                        ) : (
                          "—"
                        ),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: "arrival",
            label: "到货单",
            children: (
              <>
                {manager ? (
                  <Button
                    type="primary"
                    style={{ marginBottom: 16 }}
                    onClick={() => {
                      setArrivalRows([]);
                      setArrivalIssues([]);
                      setArrivalPreviewTotal(0);
                      setArrivalOpen(true);
                    }}
                  >
                    导入这张到货单
                  </Button>
                ) : null}
                <Table
                  rowKey="arrivalNo"
                  dataSource={arrivals}
                  locale={{
                    emptyText: manager
                      ? "还没有到货单。请导入一份清单，每行写 SKU、双数和入库日。"
                      : "还没有到货单。请主管导入清单。",
                  }}
                  pagination={{
                    current: arrivalPage,
                    pageSize: 20,
                    total: arrivalTotal,
                    onChange: setArrivalPage,
                    showTotal: (count) => `共 ${count} 张到货单`,
                  }}
                  expandable={{
                    expandedRowRender: (record) => <ArrivalLines arrivalNo={record.arrivalNo} />,
                  }}
                  columns={[
                    { title: "到货单号", dataIndex: "arrivalNo" },
                    {
                      title: "状态",
                      dataIndex: "status",
                      render: (value: string) => ARRIVAL_STATUS[value] ?? value,
                    },
                    { title: "行数", dataIndex: "lineCount" },
                    { title: "计划双数合计", dataIndex: "plannedQty" },
                    { title: "导入时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                  ]}
                />
              </>
            ),
          },
          {
            key: "import",
            label: "批量导入",
            children: manager ? (
              <>
                {importDone ? (
                  <Result
                    status="success"
                    title="这批档案已经写入"
                    subTitle={`写入 ${importDone.written} 行，跳过 ${importDone.skipped} 行。跳过的行请改完再导一次。`}
                  />
                ) : null}
                <Segmented
                  value={kind}
                  onChange={(value) => {
                    if (value === "sku" || value === "opening") setKind(value);
                    setPreviewRows([]);
                    setIssues([]);
                    setImportDone(null);
                  }}
                  options={[
                    { label: "导入 SKU", value: "sku" },
                    { label: "导入期初库存", value: "opening" },
                  ]}
                />
                <div style={{ marginTop: 16 }}>
                  <Upload.Dragger
                    accept=".csv,.xlsx"
                    maxCount={1}
                    beforeUpload={(file) => {
                      void previewImport(kind, file)
                        .then((preview) => {
                          setPreviewRows(preview.rows as Array<SkuImportRow | OpeningImportRow>);
                          setIssues(preview.issues);
                          setImportTotal(preview.total);
                          setImportDone(null);
                        })
                        .catch((error: unknown) => {
                          message.error(error instanceof Error ? error.message : "这次没有写入。");
                        });
                      return false;
                    }}
                  >
                    <p>把 CSV 或 Excel 拖到这里，或点击选择文件</p>
                  </Upload.Dragger>
                </div>
                <Space style={{ margin: "16px 0" }}>
                  <Button
                    onClick={() => {
                      const blob = new Blob([TEMPLATES[kind]], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = "导入模板.csv";
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    下载一份空白模板
                  </Button>
                  <Button
                    type="primary"
                    disabled={previewRows.length === 0}
                    onClick={() => {
                      void commitImport({ kind, rows: previewRows })
                        .then((result) => {
                          setImportDone(result);
                          load();
                        })
                        .catch((error: unknown) => {
                          message.error(error instanceof Error ? error.message : "这次没有写入。");
                        });
                    }}
                  >
                    确认写入这些档案
                  </Button>
                </Space>
                <p>共读到 {importTotal} 行</p>
                {issues.map((issue) => (
                  <p key={`${issue.row}-${issue.message}`} style={{ color: "#ff4d4f" }}>
                    {issue.message}
                  </p>
                ))}
                <Table
                  rowKey={(_row, index) => String(index)}
                  dataSource={previewRows}
                  pagination={{ pageSize: 50 }}
                  columns={Object.keys(previewRows[0] ?? {}).map((key) => ({ title: key, dataIndex: key }))}
                />
              </>
            ) : (
              <Result status="info" title="批量导入由主管操作" subTitle="请主管来导入 SKU 或期初库存。" />
            ),
          },
        ]}
      />
      <Modal
        destroyOnClose
        open={skuOpen}
        title={editing ? "修改 SKU" : "新建 SKU"}
        okText={editing ? "保存这个 SKU 的修改" : "把这个 SKU 写入档案"}
        cancelText="放弃并返回"
        onCancel={() => setSkuOpen(false)}
        onOk={() => skuForm.submit()}
      >
        {editing ? (
          <Descriptions
            size="small"
            style={{ marginBottom: 16 }}
            items={[{ key: "code", label: "内部编码", children: editing.internalCode }]}
          />
        ) : null}
        <Form
          form={skuForm}
          layout="vertical"
          onFinish={(values) => {
            const body = {
              humanCode: values.humanCode,
              name: values.name,
              color: values.color,
              sizeUs: values.sizeUs,
              sizeEu: values.sizeEu,
              trackUnit: Boolean(values.trackUnit),
            };
            const action = editing ? updateSku(editing.id, body).then(() => null) : createSku(body);
            void action
              .then((result) => {
                if (result) message.success(`这个 SKU 已写入档案。内部编码是 ${result.internalCode}。`);
                else message.success("这个 SKU 的修改已保存。");
                setSkuOpen(false);
                load();
              })
              .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这次没有写入。"));
          }}
        >
          <Form.Item label="SKU" name="humanCode" rules={[{ required: true, message: "SKU 还空着。" }]}>
            <Input disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item label="款式名称" name="name" rules={[{ required: true, message: "款式名称还空着。" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="颜色" name="color" rules={[{ required: true, message: "颜色还空着。" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="美码" name="sizeUs" rules={[{ required: true, message: "美码还空着。" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="欧码" name="sizeEu" rules={[{ required: true, message: "欧码还空着。" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="试点逐双追踪" name="trackUnit" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        destroyOnClose
        open={arrivalOpen}
        title="导入这张到货单"
        okText="写入这张到货单"
        cancelText="先不导入"
        okButtonProps={{ disabled: arrivalRows.length === 0 || arrivalIssues.length > 0 }}
        onCancel={() => setArrivalOpen(false)}
        onOk={() => {
          void commitArrival(arrivalRows)
            .then((created) => {
              message.success(
                `到货单 ${created.arrivalNo} 已写入，共 ${created.lineCount} 个 SKU。接下来到赋码打印，一次锁定全部号段。`
              );
              setArrivalOpen(false);
              load();
            })
            .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这次没有写入。"));
        }}
      >
        <Upload
          accept=".csv,.xlsx"
          maxCount={1}
          beforeUpload={(file) => {
            void previewImport("arrival", file)
              .then((preview) => {
                setArrivalRows(preview.rows as ArrivalImportRow[]);
                setArrivalIssues(preview.issues);
                setArrivalPreviewTotal(preview.total);
              })
              .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这份清单没有读出来。"));
            return false;
          }}
        >
          <Button>选择到货清单</Button>
        </Upload>
        <Button
          style={{ margin: "12px 0" }}
          onClick={() => {
            const blob = new Blob([ARRIVAL_TEMPLATE], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "到货单导入模板.csv";
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          下载到货单导入模板
        </Button>
        <p>共读到 {arrivalPreviewTotal} 行</p>
        {arrivalIssues.map((issue) => (
          <p key={`${issue.row}-${issue.message}`} style={{ color: "#ff4d4f" }}>
            {issue.message}
          </p>
        ))}
        <Table
          rowKey={(_row, index) => String(index)}
          dataSource={arrivalRows}
          pagination={{ pageSize: 50 }}
          columns={[
            { title: "SKU", dataIndex: "humanCode" },
            { title: "双数", dataIndex: "plannedQty" },
            { title: "入库日", dataIndex: "inboundDate" },
          ]}
        />
      </Modal>
    </>
  );
}

function ArrivalLines({ arrivalNo }: { arrivalNo: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ArrivalLineRow[]>([]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    void getArrival(arrivalNo, page, 50).then((detail) => {
      setRows(detail.lines);
      setTotal(detail.lineTotal);
    });
  }, [arrivalNo, page]);
  return (
    <Table
      rowKey="lineNo"
      size="small"
      dataSource={rows}
      pagination={{ current: page, pageSize: 50, total, onChange: setPage, showTotal: (count) => `共 ${count} 行` }}
      columns={[
        { title: "SKU", dataIndex: "humanCode" },
        { title: "双数", dataIndex: "plannedQty" },
        { title: "入库日", dataIndex: "inboundDate", render: (value: string) => formatDay(value) },
        { title: "批次号", dataIndex: "batchNo" },
      ]}
    />
  );
}
